import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { toast } from "sonner";
import { voteAPI } from "../lib/api";
import { Zap, Brain, Shield, Check, X, EyeOff, Trash } from "lucide-react";

const DECISIONS = [
  {
    key: "delete",
    label: "Delete",
    icon: Trash,
    color: "bg-red-50 border-red-300 text-red-800 hover:bg-red-100",
    activeColor: "bg-red-500 border-red-600 text-white",
    desc: "Not adding value. Should we stop doing this?",
  },
  {
    key: "automate",
    label: "Automate",
    icon: Zap,
    color: "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100",
    activeColor: "bg-amber-500 border-amber-600 text-white",
    desc: "AI performs the core task. Humans handle exceptions.",
  },
  {
    key: "augment",
    label: "Augment",
    icon: Brain,
    color: "bg-violet-50 border-violet-300 text-violet-800 hover:bg-violet-100",
    activeColor: "bg-violet-500 border-violet-600 text-white",
    desc: "Human-led with AI as a force multiplier.",
  },
  {
    key: "protect",
    label: "Protect",
    icon: Shield,
    color: "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100",
    activeColor: "bg-emerald-500 border-emerald-600 text-white",
    desc: "Fundamentally human. AI would diminish value.",
  },
];

export const VotingPanel = ({ stall, departmentName, boardId, myVotes, isLocked, onVoted, onClose }) => {
  const existingVote = myVotes?.find((v) => v.stall_id === stall?.id);
  const [decision, setDecision] = useState(existingVote?.decision || null);
  const [rationale, setRationale] = useState(existingVote?.rationale || "");
  const [saving, setSaving] = useState(false);

  const castVote = async (chosenDecision) => {
    if (isLocked) {
      toast.error("Board is locked");
      return;
    }
    setSaving(true);
    try {
      await voteAPI.cast(boardId, stall.id, { decision: chosenDecision, rationale });
      setDecision(chosenDecision);
      onVoted({ stall_id: stall.id, decision: chosenDecision, rationale });
      toast.success("Vote recorded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to vote");
    } finally {
      setSaving(false);
    }
  };

  if (!stall) return null;

  return (
    <Sheet open={!!stall} onOpenChange={() => onClose()}>
      <SheetContent className="w-full sm:max-w-[440px] p-0 bg-[#F8FAFC] overflow-y-auto" data-testid="voting-panel">
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-200 px-6 py-4">
          <SheetHeader className="text-left">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs text-slate-500 border-slate-200 font-medium">
                  {departmentName}
                </Badge>
                <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-[10px]">
                  <EyeOff className="w-2.5 h-2.5 mr-1" />
                  Blind Vote
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <SheetTitle className="font-heading text-xl text-slate-900 mt-2 pr-6">
              {stall.name}
            </SheetTitle>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">{stall.description}</p>
          </SheetHeader>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <EyeOff className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Your Vote is Private</span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              Other participants cannot see your vote until the facilitator reveals all votes. Vote honestly based on your expertise.
            </p>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">
              Cast Your Vote
            </label>
            <div className="space-y-2">
              {DECISIONS.map((d) => (
                <button
                  key={d.key}
                  data-testid={`vote-${d.key}-btn`}
                  onClick={() => castVote(d.key)}
                  disabled={isLocked || saving}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 font-medium text-sm transition-colors ${
                    decision === d.key ? d.activeColor : d.color
                  } ${isLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <d.icon className="w-5 h-5" />
                  <div className="text-left flex-1">
                    <div className="font-heading font-semibold">{d.label}</div>
                    <div className={`text-xs mt-0.5 ${decision === d.key ? "opacity-80" : "opacity-60"}`}>
                      {d.desc}
                    </div>
                  </div>
                  {decision === d.key && <Check className="w-5 h-5" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">
              Rationale (optional)
            </label>
            <Textarea
              data-testid="vote-rationale-textarea"
              placeholder="Why did you vote this way? This will be shared when votes are revealed."
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              disabled={isLocked}
              className="min-h-[80px] bg-white border-slate-200 resize-none text-sm"
            />
            {rationale && decision && (
              <Button
                variant="outline"
                size="sm"
                data-testid="update-rationale-btn"
                onClick={() => castVote(decision)}
                disabled={saving}
                className="mt-2 text-xs"
              >
                {saving ? "Saving..." : "Update Rationale"}
              </Button>
            )}
          </div>

          {decision && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center animate-fade-in">
              <Check className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
              <p className="text-sm font-medium text-emerald-800">
                Vote recorded: <span className="capitalize">{decision}</span>
              </p>
              <p className="text-xs text-emerald-600 mt-0.5">You can change your vote until the reveal.</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default VotingPanel;
